import React, { useContext, useEffect, useState } from 'react';
import {
  Image, StyleSheet, Text, TextInput, View,
} from 'react-native';
import { observer } from 'mobx-react-lite';
import { mutate } from 'swr';

import ScreenLayout from './ScreenLayout';
import Section from '../components/Section';
import Button from '../components/Button';
import Style from '../style';
import I18n from '../i18n';
import { requestMfaSetup, verifyMfaCode } from '../api/Mfa';
import { RootStoreContext } from '../stores/RootStore';
import { fetchUser } from '../api/Users';
import AccessErrorScreen from './AccessErrorScreen';
import { screenAccess } from '../navigation/AccessRules';

const BARCODE_SIZE = 152;
const MFA_SETUP_TIMEOUT_SECONDS = 120;

type MfaSetup = {
  barcodeUri: string,
  expiresAt: number,
};

const formatRemainingTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const SecurityScreen = observer(() => {
  const store = useContext(RootStoreContext);
  const myId = store.auth.userId;
  if (!store.auth.checkAccess(screenAccess.Security)) return <AccessErrorScreen />;
  const fetchUserData = fetchUser(myId);
  const { data: myDetails } = fetchUserData.swr;
  const fetchUserUrl = fetchUserData.url;
  const mfaTotpEnabled = !!myDetails?.mfaTotpEnabled;
  const [mfaSetup, setMfaSetup] = useState<MfaSetup | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupNotice, setSetupNotice] = useState('');
  const [secondsRemaining, setSecondsRemaining] = useState(MFA_SETUP_TIMEOUT_SECONDS);

  useEffect(() => {
    if (typeof myDetails?.mfaTotpEnabled === 'boolean') {
      store.auth.setMfaTotpEnabled(myDetails.mfaTotpEnabled);
    }
  }, [myDetails?.mfaTotpEnabled, store.auth]);

  useEffect(() => {
    if (!mfaSetup) return undefined;

    const updateRemainingTime = () => {
      const remaining = Math.max(0, Math.ceil((mfaSetup.expiresAt - Date.now()) / 1000));
      setSecondsRemaining(remaining);

      if (remaining === 0) {
        setMfaSetup(null);
        setVerificationCode('');
        setIsLoading(false);
        setError('');
        setSetupNotice(String(I18n.t('ui.security.mfaSetupExpired')));
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [mfaSetup]);

  const startMfaSetup = async () => {
    if (!myId) return;

    setIsLoading(true);
    setError('');
    setSetupNotice('');
    try {
      const setup = await requestMfaSetup(myId);
      setSecondsRemaining(MFA_SETUP_TIMEOUT_SECONDS);
      setMfaSetup({
        barcodeUri: setup.qrDataUrl,
        expiresAt: Date.now() + MFA_SETUP_TIMEOUT_SECONDS * 1000,
      });
      setVerificationCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(I18n.t('ui.security.mfaVerificationFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  const cancelMfaSetup = () => {
    setMfaSetup(null);
    setVerificationCode('');
    setError('');
    setSetupNotice('');
  };

  const confirmMfaCode = async () => {
    if (!myId) return;

    if (mfaSetup && Date.now() >= mfaSetup.expiresAt) {
      setMfaSetup(null);
      setVerificationCode('');
      setError('');
      setSetupNotice(String(I18n.t('ui.security.mfaSetupExpired')));
      return;
    }

    if (!mfaSetup || !/^\d{6}$/.test(verificationCode)) {
      setError(String(I18n.t('ui.security.mfaInvalidCode')));
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await verifyMfaCode(myId, verificationCode);
      store.auth.setMfaTotpEnabled(true);
      if (myDetails) await mutate(fetchUserUrl, { ...myDetails, mfaTotpEnabled: true }, false);
      setMfaSetup(null);
      setVerificationCode('');
      setSetupNotice('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(I18n.t('ui.security.mfaVerificationFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ScreenLayout>
      <View style={styles.container}>
        <Section
          icon={Style.Icon.Key}
          title={String(I18n.t('ui.security.title'))}
          key="security"
        >
          <View style={styles.card}>
            <View style={styles.cardContent}>
              <View style={styles.titleRow}>
                <Text style={styles.heading}>{String(I18n.t('ui.security.mfaTitle'))}</Text>
                <Text
                  style={[
                    styles.status,
                    mfaTotpEnabled ? styles.statusEnabled : styles.statusDisabled,
                  ]}
                >
                  {String(I18n.t(`ui.security.${mfaTotpEnabled ? 'enabled' : 'disabled'}`))}
                </Text>
              </View>
              <Text style={styles.description}>{String(I18n.t('ui.security.mfaDescription'))}</Text>
              {!!setupNotice && !mfaSetup && <Text style={styles.notice}>{setupNotice}</Text>}
              {mfaSetup && (
                <View style={styles.setupPanel}>
                  <View style={styles.barcodeBox}>
                    <Image
                      source={{ uri: mfaSetup.barcodeUri }}
                      style={styles.barcode}
                      resizeMode="contain"
                    />
                  </View>
                  <View style={styles.setupContent}>
                    <Text style={styles.setupTitle}>
                      {String(I18n.t('ui.security.mfaScanTitle'))}
                    </Text>
                    <Text style={styles.description}>
                      {String(I18n.t('ui.security.mfaScanDescription'))}
                    </Text>
                    <Text style={styles.timeout}>
                      {String(I18n.t('ui.security.mfaSetupTimeout', {
                        time: formatRemainingTime(secondsRemaining),
                      }))}
                    </Text>
                    <TextInput
                      value={verificationCode}
                      onChangeText={(text) => {
                        setVerificationCode(text.replace(/\D/g, '').slice(0, 6));
                        setError('');
                      }}
                      placeholder={String(I18n.t('ui.security.mfaCodePlaceholder'))}
                      placeholderTextColor={Style.Color.Gray300}
                      keyboardType="number-pad"
                      maxLength={6}
                      style={[styles.codeInput, error && styles.codeInputError]}
                      testID="Security.MfaCodeInput"
                    />
                    {!!error && <Text style={styles.error}>{error}</Text>}
                    <View style={styles.setupActions}>
                      <View style={styles.verifyButtonPad}>
                        <Button
                          title={String(I18n.t('ui.security.verifyMfa'))}
                          onPress={confirmMfaCode}
                          loading={isLoading}
                          disable={verificationCode.length !== 6}
                          testId="Security.VerifyMfaButton"
                          icon="Key"
                        />
                      </View>
                      <View style={styles.cancelButtonPad}>
                        <Button
                          title={String(I18n.t('ui.security.cancelMfa'))}
                          onPress={cancelMfaSetup}
                          variant="outline"
                          disable={isLoading}
                          testId="Security.CancelMfaButton"
                        />
                      </View>
                    </View>
                  </View>
                </View>
              )}
            </View>
            {!mfaSetup && !mfaTotpEnabled && (
              <View style={styles.buttonPad}>
                <Button
                  btnStyle={styles.btn}
                  btnStyleHovered={styles.btnHovered}
                  onPress={startMfaSetup}
                  loading={isLoading}
                  title={String(I18n.t('ui.security.enableMfa'))}
                  testId="Security.MfaButton"
                  icon="Key"
                />
              </View>
            )}
          </View>
        </Section>
      </View>
    </ScreenLayout>
  );
});

export default SecurityScreen;

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignItems: 'center',
    flex: 1,
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 22,
    paddingVertical: 18,
    borderBottomColor: Style.Color.Gray200,
    borderBottomWidth: 1,
  },
  cardContent: {
    flex: 1,
    paddingRight: 24,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  heading: {
    ...Style.Text.Caption,
    color: Style.Color.Black,
    marginBottom: 8,
  },
  description: {
    ...Style.Text.Normal,
    color: Style.Color.Gray600,
    maxWidth: 560,
  },
  buttonPad: {
    width: 180,
    height: 40,
    marginTop: 2,
  },
  btn: {
    marginLeft: 4,
  },
  btnHovered: {
    marginLeft: 4,
    backgroundColor: Style.Color.Primary,
  },
  status: {
    ...Style.Text.SmallBold,
    marginLeft: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  statusEnabled: {
    color: Style.Color.Green,
    backgroundColor: Style.Color.Gray100,
  },
  statusDisabled: {
    color: Style.Color.Gray400,
    backgroundColor: Style.Color.Gray100,
  },
  setupPanel: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: Style.Color.Gray200,
    borderRadius: 8,
    backgroundColor: Style.Color.Gray100,
  },
  barcodeBox: {
    width: BARCODE_SIZE + 20,
    height: BARCODE_SIZE + 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Style.Color.White,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Style.Color.Gray200,
    marginRight: 18,
  },
  barcode: {
    width: BARCODE_SIZE,
    height: BARCODE_SIZE,
  },
  setupContent: {
    flex: 1,
    maxWidth: 460,
  },
  setupTitle: {
    ...Style.Text.NormalBold,
    color: Style.Color.Black,
    marginBottom: 6,
  },
  timeout: {
    ...Style.Text.SmallBold,
    color: Style.Color.Gray600,
    marginTop: 10,
  },
  codeInput: {
    width: 180,
    height: 40,
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Style.Color.Primary,
    outlineColor: Style.Color.Primary,
    ...Style.Text.Code,
    color: Style.Color.Primary,
    backgroundColor: Style.Color.White,
    paddingHorizontal: 10,
    fontSize: 18,
    letterSpacing: 2,
    marginTop: 14,
  },
  codeInputError: {
    borderColor: Style.Color.Tertiary,
    outlineColor: Style.Color.Tertiary,
    color: Style.Color.Tertiary,
  },
  error: {
    ...Style.Text.Tiny,
    color: Style.Color.Tertiary,
    marginTop: 4,
  },
  notice: {
    ...Style.Text.Small,
    color: Style.Color.Tertiary,
    marginTop: 8,
  },
  setupActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 14,
  },
  verifyButtonPad: {
    width: 160,
    height: 40,
    marginRight: 12,
  },
  cancelButtonPad: {
    width: 120,
    height: 40,
  },
});
